import React from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { Team } from '../../types/organization.types';
import { useTeamDepartments, useUpdateTeam } from '../../hooks/useOrgQueries';
import { useToast } from '../../hooks/useToast';
import { LedgerSkeleton } from '../shell/LedgerSkeleton';
import { cn } from '../../lib/cn';

// Same date rules as RegisterPeek/TaskPeek: local time for timestamps.
const stampLocal = (iso: string | null | undefined): string | null =>
  iso ? format(new Date(iso), 'MMM d, yyyy').toUpperCase() : null;

interface TeamPeekProps {
  team: Team;
  onClose: () => void;
  onEdit: (id: string) => void;
  onDelete: (team: Team) => void;
}

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <div className="cap-label">{label}</div>
    <div className="mt-0.5 text-scale-1 text-ink">{children}</div>
  </div>
);

export const TeamPeek: React.FC<TeamPeekProps> = ({ team, onClose, onEdit, onDelete }) => {
  const departments = useTeamDepartments(team.id);
  const update = useUpdateTeam();
  const toast = useToast();

  const onSaveError = () => toast.error('Save failed');
  const updatedStamp = stampLocal(team.updatedAt);

  return (
    <aside aria-label="Team peek" className="border-l border-ink bg-paper-raised p-5">
      <div className="flex items-baseline justify-between">
        <span className="cap-label">TEAM</span>
        <span className="flex gap-3">
          <button type="button" onClick={() => onEdit(team.id)} className="cap-label cursor-pointer !text-accent hover:underline">
            Edit ↗
          </button>
          <button type="button" onClick={() => onDelete(team)} className="cap-label cursor-pointer text-ink-muted hover:!text-sev-critical">
            Delete
          </button>
          <button type="button" aria-label="Close peek" onClick={onClose} className="cursor-pointer text-scale-0 text-ink-muted hover:text-ink">
            ✕
          </button>
        </span>
      </div>

      <h2 className="mt-1.5 font-serif text-scale-4 font-medium text-ink">{team.name}</h2>
      {team.companyName && <p className="mb-3 text-scale-1 text-ink-muted">{team.companyName}</p>}
      <div className="border-b border-ink" />

      <div className="grid grid-cols-2 gap-x-4 gap-y-2.5 py-3">
        <Field label="Members">
          <span className="font-mono">{team.usersCount}</span>
        </Field>
        <Field label="Status">
          <button
            type="button"
            role="switch"
            aria-checked={team.isActive}
            aria-label="Active"
            disabled={update.isPending}
            onClick={() =>
              update.mutate({ id: team.id, data: { isActive: !team.isActive } }, { onError: onSaveError })
            }
            className="flex cursor-pointer items-center gap-2 disabled:cursor-default"
          >
            <span aria-hidden className={cn('h-3 w-3 border border-ink', team.isActive && 'bg-ink')} />
            <span className="text-scale-1 text-ink">{team.isActive ? 'Active' : 'Inactive'}</span>
          </button>
        </Field>
      </div>

      {team.description && (
        <p className="border-t border-rule-faint py-3 text-scale-1 text-ink-muted">{team.description}</p>
      )}

      <div className="cap-label mt-4 border-b border-ink pb-0.5">Departments</div>
      {departments.isPending ? (
        <LedgerSkeleton lines={3} className="mt-2" />
      ) : (departments.data ?? []).length === 0 ? (
        <p className="pt-1.5 font-serif text-scale-1 italic text-ink-muted">No departments yet.</p>
      ) : (
        <ul>
          {(departments.data ?? []).map((department) => (
            <li
              key={department.id}
              className="flex items-baseline justify-between border-b border-rule-faint py-1.5 text-scale-1"
            >
              <span className="text-ink">{department.name}</span>
              <span className="font-mono text-ink-muted">{department.usersCount}</span>
            </li>
          ))}
        </ul>
      )}
      <Link
        to={`/organizations/departments?team=${team.id}`}
        className="cap-label mt-1.5 inline-block cursor-pointer !text-accent hover:underline"
      >
        View departments ↗
      </Link>

      <div className="cap-label mt-4 border-b border-ink pb-0.5">History</div>
      <div className="pt-1.5 font-serif text-scale-1 italic text-ink-muted">
        <div>Recorded {stampLocal(team.createdAt)}</div>
        {updatedStamp && <div>Updated {updatedStamp}</div>}
      </div>
    </aside>
  );
};
